import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
} from '@mui/material';
import marketingTheme from '../themes/marketingTheme';

const RoboticsConsulting = () => {
  const capabilityGroups = [
    [
      "Robot Perception & Vision",
      [
        "Computer vision for object detection and recognition",
        "3D depth sensing and spatial understanding",
        "Visual-inertial odometry and SLAM",
        "Multi-sensor fusion for robust perception"
      ]
    ],
    [
      "Machine Learning for Robotics",
      [
        "Reinforcement learning for robot control",
        "Imitation learning and demonstration-based training",
        "Deep learning for sensorimotor control",
        "Adaptive behavior learning"
      ]
    ],
    [
      "Manipulation & Grasping",
      [
        "Robotic grasping and manipulation planning",
        "Force/torque sensing and control",
        "Dexterous manipulation strategies",
        "Pick-and-place optimization"
      ]
    ],
    [
      "Navigation & Planning",
      [
        "Path planning and motion control",
        "Obstacle avoidance algorithms",
        "Multi-robot coordination",
        "Dynamic environment adaptation"
      ]
    ]
  ];

  return (
    <Box sx={{ backgroundColor: '#24242a', minHeight: '100vh', color: 'white' }}>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: 'url(/images/pic08.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          py: 12,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 'bold',
              mb: 2,
              color: 'white',
              textAlign: 'center',
            }}
          >
            Cognitive Robotics
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto',
            }}
          >
            Guide robots with vision and algorithms to enable smarter decision making on the production floor.
          </Typography>
        </Container>
      </Box>

      {/* Overview Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={8}>
            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
              We make robots smarter through learning algorithms. We mobilise research capabilities in the areas of robot perception and machine learning into your industrial application. Enabling more adaptive production.
            </Typography>

            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <img
                src="/images/traversal_perception_cognition_actuation.png"
                alt="Perception Cognition Actuation"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </Box>

            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
              We specialize in applying machine learning and perception algorithms to make robotic deployments more efficient and effective.
            </Typography>

            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <video
                style={{ maxWidth: '100%', width: '600px' }}
                autoPlay
                muted
                loop
              >
                <source src="/videos/camera_perception.mp4" type="video/mp4" />
              </video>
            </Box>

            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 2 }}>
              We begin with a needs analysis and an assessment of the value and constraints. We advise on component selection, processes, and algorithms. We generate an integration plan to ensure that the components are integrated cohesively.
            </Typography>

            <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
              We generate budgets, work breakdown structures, perform risk assessments and provide proposals, designs and recommendations. We facilitate research and manage the R&D process, including managing reporting for the R&D Tax Incentive.
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'sticky', top: 100 }}>
              <img
                src="/images/fetch_grasping_example.png"
                alt="Robotics Consulting"
                style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Capabilities Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          sx={{
            fontWeight: 'bold',
            mb: 6,
            textAlign: 'center',
            color: marketingTheme.palette.primary.main,
          }}
        >
          Our Capabilities
        </Typography>

        <Grid container spacing={4}>
          {capabilityGroups.map((group, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                sx={{
                  p: 4,
                  backgroundColor: '#2d2d33',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  height: '100%',
                }}
              >
                <Typography
                  variant="h5"
                  component="h3"
                  sx={{
                    fontWeight: 'bold',
                    mb: 3,
                    color: marketingTheme.palette.primary.main,
                  }}
                >
                  {group[0]}
                </Typography>
                <List>
                  {group[1].map((capability, capIndex) => (
                    <ListItem key={capIndex} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={capability}
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: 'white',
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#2d2d33', py: 8 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 'bold',
                mb: 3,
                color: marketingTheme.palette.primary.main,
              }}
            >
              Ready to Make Your Robots Smarter?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                mb: 4,
                color: 'rgba(255, 255, 255, 0.8)',
              }}
            >
              Let's discuss how cognitive robotics can transform your production floor.
            </Typography>
            <Button
              variant="contained"
              size="large"
              href="/contact"
              sx={{
                backgroundColor: marketingTheme.palette.primary.main,
                color: '#000',
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                '&:hover': {
                  backgroundColor: '#d4a028',
                },
              }}
            >
              Get Started
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default RoboticsConsulting;
